import {isBlockingPathBuilding, isReconstructionProhibited} from './tokens.js';
import {
  selectCitiesCache,
  selectCurrentPlayer,
  selectGridConfigState,
  selectHexes,
  selectPendingBuildings,
  selectUnits
} from "../redux/reducers/sharedStateReducer/selectors";
import {getCityHexKeysForPlayerAndEnemies} from "./gameState/getCityHexKeysForPlayerAndEnemies.js";
import {checkIfHexBlockedByEnemyUnit} from "./gameState/checkIfHexBlockedByEnemyUnit.js";
import {checkIfAnyQuarter} from "./buildings/checkIfAnyQuarter";
import {TERRAIN_TOKENS} from "../constants/tokens";
import {HEX_DIRECTIONS} from "../constants/geometry";
import {coordsToKey} from "./coords/coordsToKey";

// Ключи ячеек под ключами городов, в формате «q,r» из чисел. Координаты, не
// ставшие числами, пропускаются: сравнение по координатам их не находило бы
const toCoordinateKeySet = (hexKeys) => {
  const keys = new Set();
  hexKeys.forEach((hexKey) => {
    const [q, r] = hexKey.split(',').map(Number);
    if (!Number.isNaN(q) && !Number.isNaN(r)) {
      keys.add(coordsToKey(q, r));
    }
  });
  return keys;
};

/**
 * Проверка «ячейка заблокирована вражеским отрядом» с тем же ответом, что
 * checkIfHexBlockedByEnemyUnit(ключ, null, ...): отряд стоит на самой ячейке
 * или на смежной. Ключи вражеских отрядов собираются в Set один раз, а ответ
 * запоминается по ключу ячейки — поиск путей спрашивает одни и те же ячейки
 * много раз
 * @returns {(hexKey: string) => boolean}
 */
const createEnemyUnitBlockCheck = (units, currentPlayerId) => {
  const enemyUnitHexKeys = new Set(units.filter((unit) => unit.ownerId !== currentPlayerId).map((unit) => unit.hexKey));
  if (enemyUnitHexKeys.size === 0) {
    return () => false;
  }

  const isBlockedByHexKey = new Map();
  return (hexKey) => {
    let isBlocked = isBlockedByHexKey.get(hexKey);
    if (isBlocked === undefined) {
      const [q, r] = hexKey.split(',').map(Number);
      isBlocked = enemyUnitHexKeys.has(hexKey)
        || HEX_DIRECTIONS.some(([dq, dr]) => enemyUnitHexKeys.has(coordsToKey(q + dq, r + dr)));
      isBlockedByHexKey.set(hexKey, isBlocked);
    }
    return isBlocked;
  };
};

/**
 * Класс для расчета доступных ячеек для строительства в фазе строительства
 */
export class ConstructionAccessibility {
  constructor(hexMath) {
    this.hexMath = hexMath;
  }

  /**
   * Найти все доступные ячейки для строительства для игрока
   * @returns {Array} Массив ключей доступных ячеек
   */
  findAccessibleCellsForConstruction(state, player = null, options = {}) {
    const isAccessible = this.createAccessibleCellForConstructionPredicate(state, player, options);
    const {radius} = selectGridConfigState(state);
    const accessibleCells = [];

    // Обходим все ячейки сетки
    for (let q = -radius; q <= radius; q++) {
      for (let r = -radius; r <= radius; r++) {
        const s = -q - r;
        if (Math.abs(s) <= radius) {
          const key = `${q},${r}`;
          if (isAccessible(key)) {
            accessibleCells.push(key);
          }
        }
      }
    }

    return accessibleCells;
  }

  /**
   * Проверка «ячейка доступна для строительства» — ровно тот же ответ, что
   * findAccessibleCellsForConstruction(...).includes(hexKey): подготовка
   * (города, отряды, отложенные строения) делается один раз. Ответ для ячейки
   * от других ячеек не зависит, поэтому полный расчет — это просто обход сетки
   * с этой проверкой. Нужна и там, где интересна одна ячейка, а не все поле
   * (перепроверка плана строительства)
   *
   * Правило пути: от ячейки можно идти по смежным ячейкам со строениями, не
   * заблокированным вражескими отрядами, не непроходимым (местность) и не
   * чужим городским; ячейка доступна, если так доходим до своего городского
   * строения (см. checkIfPassable ниже). Раньше путь искался заново от каждой
   * ячейки поля — на телефоне это полсекунды на шаг бота. Теперь проходимые
   * ячейки разбиваются на связные области один раз (лениво, по мере вопросов),
   * и ячейка доступна, если она сама — свое городское строение или рядом с ней
   * есть проходимая ячейка из области со своим городским строением. Это тот же
   * ответ: путь от ячейки всегда начинается с шага на проходимого соседа, а
   * дальше идет внутри его области; возвращаться в саму ячейку ему не нужно —
   * от последнего посещения можно было сразу шагнуть к следующему соседу
   * @returns {(hexKey: string) => boolean}
   */
  createAccessibleCellForConstructionPredicate(state, player = null, options = {}) {
    const {ignoreEnemyUnitsBlocking = false} = options;

    const hexes = selectHexes(state);
    const gridConfig = selectGridConfigState(state)
    const units = selectUnits(state);
    const currentPlayer = player || selectCurrentPlayer(state);
    const pendingBuildings = selectPendingBuildings(state);
    const citiesCache = selectCitiesCache(state);

    const {radius} = gridConfig;

    // Получаем hex ключи городов игрока и соперников через новую функцию
    const {playerCityHexKeys, enemyCityHexKeys} = getCityHexKeysForPlayerAndEnemies(citiesCache, currentPlayer);

    if (playerCityHexKeys.length === 0) {
      return () => false; // У игрока нет городов
    }

    // Заблокирована ли ячейка вражеским отрядом (без учета блокировки — никогда)
    const checkIfEnemyBlocked = ignoreEnemyUnitsBlocking
      ? () => false
      : createEnemyUnitBlockCheck(units, currentPlayer.id);

    // Свои городские строения, кроме заблокированных отрядами противника
    const playerBuildingKeys = toCoordinateKeySet(
      playerCityHexKeys.filter((hexKey) => !checkIfEnemyBlocked(hexKey))
    );

    if (playerBuildingKeys.size === 0) {
      return () => false; // Все города игрока заблокированы врагом
    }

    const otherPlayersBuildingKeys = toCoordinateKeySet(enemyCityHexKeys);
    const pendingHexKeys = new Set(pendingBuildings.map(action => action.hexKey));

    // Через ячейку можно пройти: в ней строение, это не местность, она не
    // заблокирована отрядом противника и не принадлежит чужому городу (своя —
    // можно, даже если она числится и в чужом городе)
    const passableByKey = new Map();
    const checkIfPassable = (hexKey) => {
      let isPassable = passableByKey.get(hexKey);
      if (isPassable === undefined) {
        const token = hexes[hexKey];
        isPassable = Boolean(token)
          && !isBlockingPathBuilding(token)
          && !checkIfEnemyBlocked(hexKey)
          && (playerBuildingKeys.has(hexKey) || !otherPlayersBuildingKeys.has(hexKey));
        passableByKey.set(hexKey, isPassable);
      }
      return isPassable;
    };

    // Связные области проходимых ячеек: ключ -> есть ли в области свое
    // городское строение. Область размечается целиком при первом вопросе
    const areaHasPlayerBuildingByKey = new Map();
    const checkIfAreaHasPlayerBuilding = (startKey, startQ, startR) => {
      const known = areaHasPlayerBuildingByKey.get(startKey);
      if (known !== undefined) {
        return known;
      }

      const areaKeys = [startKey];
      const areaCoords = [[startQ, startR]];
      const inArea = new Set(areaKeys);
      let hasPlayerBuilding = false;
      for (let index = 0; index < areaCoords.length; index++) {
        const [q, r] = areaCoords[index];
        if (playerBuildingKeys.has(areaKeys[index])) {
          hasPlayerBuilding = true;
        }
        for (const [nq, nr] of this.hexMath.getNeighborsFlatTop(q, r)) {
          const neighborKey = `${nq},${nr}`;
          if (!inArea.has(neighborKey) && checkIfPassable(neighborKey)) {
            inArea.add(neighborKey);
            areaKeys.push(neighborKey);
            areaCoords.push([nq, nr]);
          }
        }
      }

      areaKeys.forEach((hexKey) => areaHasPlayerBuildingByKey.set(hexKey, hasPlayerBuilding));
      return hasPlayerBuilding;
    };

    return (key) => {
      const [q, r] = key.split(',').map(Number);
      // Только ячейки сетки — как и в обходе findAccessibleCellsForConstruction
      if (Math.abs(q) > radius || Math.abs(r) > radius || Math.abs(-q - r) > radius) {
        return false;
      }

      if (isReconstructionProhibited(hexes[key])) {
        return false;
      }

      if (pendingHexKeys.has(key)) {
        return false;
      }

      // Сама ячейка: заблокированная отрядом противника недоступна, свое
      // городское строение — доступно
      const startKey = `${q},${r}`;
      if (checkIfEnemyBlocked(startKey)) {
        return false;
      }
      if (playerBuildingKeys.has(startKey)) {
        return true;
      }

      // Иначе нужен проходимый сосед из области со своим городским строением
      return this.hexMath.getNeighborsFlatTop(q, r).some(([nq, nr]) => {
        const neighborKey = `${nq},${nr}`;
        return checkIfPassable(neighborKey) && checkIfAreaHasPlayerBuilding(neighborKey, nq, nr);
      });
    };
  }

  /**
   * Найти доступные ячейки для строительства в первом раунде
   * Доступны только ячейки смежные с кварталами игрока (в citiesCache и pendingBuildings)
   * @returns {Array} Массив ключей доступных ячеек
   */
  findAccessibleConstructionCellsForFirstRound(state, player = null) {
    const hexes = selectHexes(state);
    const gridConfig = selectGridConfigState(state);
    const pendingBuildings = selectPendingBuildings(state);
    const citiesCache = selectCitiesCache(state);
    const currentPlayer = player || selectCurrentPlayer(state);
    const units = selectUnits(state);

    const {radius} = gridConfig;
    const accessibleCells = new Set();

    // Получаем все кварталы игрока из citiesCache
    const {playerCityHexKeys} = getCityHexKeysForPlayerAndEnemies(citiesCache, currentPlayer);

    // Собираем все позиции кварталов игрока (существующие + ожидающие строительства)
    const playerQuarterPositions = new Set();

    // Добавляем существующие кварталы
    playerCityHexKeys.forEach(hexKey => {
      if (checkIfAnyQuarter(hexes[hexKey])) {
        playerQuarterPositions.add(hexKey);
      }
    });

    // Добавляем кварталы из pendingBuildings
    pendingBuildings.forEach(action => {
      if (checkIfAnyQuarter(action.token)) {
        playerQuarterPositions.add(action.hexKey);
      }
    });

    if (playerQuarterPositions.size === 0) {
      return [];
    }

    // Для каждого квартала получаем соседние ячейки
    for (const quarterKey of playerQuarterPositions) {
      // Квартал, занятый или блокированный смежным отрядом (в т.ч. нейтральным),
      // не может служить опорой для строительства — как и в остальных раундах
      if (checkIfHexBlockedByEnemyUnit(quarterKey, null, currentPlayer.id, units, null, null, null)) {
        continue;
      }

      const [q, r] = quarterKey.split(',').map(Number);
      const neighbors = this.hexMath.getNeighborsFlatTop(q, r);

      for (const [nq, nr] of neighbors) {
        const neighborKey = `${nq},${nr}`;

        // Проверяем что ячейка находится в пределах сетки
        const s = -nq - nr;
        if (Math.abs(s) > radius) continue;

        // Проверяем что ячейка не заблокирована для реконструкции
        if (isReconstructionProhibited(hexes[neighborKey])) continue;

        // Проверяем что в ячейке нет уже запланированного строения
        const isPending = pendingBuildings.some(action => action.hexKey === neighborKey);
        if (isPending) continue;

        // Ячейка, занятая или смежная с отрядом противника (в т.ч. нейтральным),
        // недоступна для строительства — то же правило, что и в остальных раундах
        if (checkIfHexBlockedByEnemyUnit(neighborKey, null, currentPlayer.id, units, null, null, null)) continue;

        accessibleCells.add(neighborKey);
      }
    }

    return Array.from(accessibleCells);
  }

  /**
   * Найти все ячейки достижимые из стартовой ячейки за указанное количество ходов
   * @param {string} startKey - ключ стартовой ячейки в формате 'q,r'
   * @param {number} maxDepth - максимальная глубина поиска (количество ходов)
   * @param hexes
   * @returns {Set<string>} набор ключей достижимых ячеек
   */
  findReachableCells(startKey, maxDepth, hexes) {
    const [startQ, startR] = startKey.split(',').map(Number);

    const visited = new Map();
    const queue = [[startQ, startR, 0]];

    visited.set(startKey, 0);

    // Очередь по указателю, а не shift(): тот же порядок обхода без сдвига
    // массива на каждом шаге
    for (let queueIndex = 0; queueIndex < queue.length; queueIndex++) {
      const [q, r, depth] = queue[queueIndex];

      if (depth >= maxDepth) {
        continue;
      }

      const neighbors = this.hexMath.getNeighborsFlatTop(q, r);

      for (const [nq, nr] of neighbors) {
        const neighborKey = `${nq},${nr}`;

        if (visited.has(neighborKey)) {
          continue;
        }

        if (hexes[neighborKey] === TERRAIN_TOKENS.MOUNTAIN) {
          continue;
        }

        visited.set(neighborKey, depth + 1);
        queue.push([nq, nr, depth + 1]);
      }
    }

    return new Set(visited.keys());
  }
}